import { useCallback, useEffect, useState } from 'react';
import './App.css';
import { CandidateProfile, FocusArea } from './services/cvGenerator';
import { cvDataCollection } from './api/cv/cvDataCollectionFactory';
import HomePage from './components/HomePage';
import CvPage from './components/CvPage';
import ContactPage from './components/ContactPage';
import { Language, useLanguage } from './i18n/LanguageContext';
import mouseItLogo from './img/logos/MouseIT.png';
import mouseIcon from './img/logos/MouseIconWhite.png';

type Page = 'home' | 'cv' | 'contact';

const isLanguage = (value: string): value is Language =>
  value === 'en' || value === 'nl';

const isPage = (value: string): value is Page =>
  value === 'home' || value === 'cv' || value === 'contact';

const toPath = (language: Language, page: Page): string =>
  page === 'home' ? `/${language}` : `/${language}/${page}`;

const parsePath = (pathname: string): { language: Language; page: Page; normalizedPath: string } => {
  const parts = pathname.split('/').filter(Boolean);

  const language = isLanguage(parts[0]) ? parts[0] : 'en';
  const page = isPage(parts[1]) ? parts[1] : 'home';

  return {
    language,
    page,
    normalizedPath: toPath(language, page),
  };
};

const setLinkTag = (rel: string, href: string, hreflang?: string): void => {
  let selector = `link[rel="${rel}"]`;
  if (hreflang) {
    selector += `[hreflang="${hreflang}"]`;
  }

  let link = document.head.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    if (hreflang) {
      link.hreflang = hreflang;
    }
    document.head.appendChild(link);
  }
  link.href = href;
};

const App = () => {
  const { t, language, setLanguage } = useLanguage();
  const [activePage, setActivePage] = useState<Page>('home');
  const [candidateProfile, setCandidateProfile] = useState<CandidateProfile | null>(null);
  const [availableFocusAreas, setAvailableFocusAreas] = useState<FocusArea[]>([]);
  const [availableTechnologies, setAvailableTechnologies] = useState<string[]>([]);
  const [dataError, setDataError] = useState<string | null>(null);

  const navigate = useCallback(
    (nextLanguage: Language, nextPage: Page, replaceHistory = false): void => {
      setLanguage(nextLanguage);
      setActivePage(nextPage);

      const nextPath = toPath(nextLanguage, nextPage);
      if (window.location.pathname !== nextPath) {
        if (replaceHistory) {
          window.history.replaceState(null, '', nextPath);
        } else {
          window.history.pushState(null, '', nextPath);
        }
      }
    },
    [setLanguage]
  );

  useEffect(() => {
    const syncFromUrl = (replaceHistory = false): void => {
      const parsed = parsePath(window.location.pathname);
      navigate(parsed.language, parsed.page, replaceHistory);
    };

    syncFromUrl(true);

    const handlePopState = (): void => {
      syncFromUrl(true);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  useEffect(() => {
    let isCancelled = false;

    const loadCandidateProfile = async (): Promise<void> => {
      try {
        const [profile, focusAreas, technologies] = await Promise.all([
          cvDataCollection.getCandidateProfile(language),
          cvDataCollection.getAvailableFocusAreas(language),
          cvDataCollection.getAvailableTechnologies(language),
        ]);
        if (!isCancelled) {
          setCandidateProfile(profile);
          setAvailableFocusAreas(focusAreas);
          setAvailableTechnologies(technologies);
          setDataError(null);
        }
      } catch (error) {
        if (!isCancelled) {
          const message =
            error instanceof Error
              ? error.message
              : t.errors.failedToLoadProfile;
          setDataError(message);
        }
      }
    };

    void loadCandidateProfile();

    return () => {
      isCancelled = true;
    };
  }, [language, t.errors.failedToLoadProfile]);

  useEffect(() => {
    document.documentElement.lang = language;

    const pageTitle =
      activePage === 'home'
        ? t.seo.homeTitle
        : activePage === 'cv'
          ? t.seo.cvTitle
          : t.seo.contactTitle;

    const pageDescription =
      activePage === 'home'
        ? t.seo.homeDescription
        : activePage === 'cv'
          ? t.seo.cvDescription
          : t.seo.contactDescription;

    document.title = `${pageTitle} | ${t.seo.siteName}`;

    let metaDescription = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = pageDescription;

    const origin = window.location.origin;
    const canonicalPath = toPath(language, activePage);
    const canonicalUrl = `${origin}${canonicalPath}`;

    setLinkTag('canonical', canonicalUrl);
    setLinkTag('alternate', `${origin}${toPath('en', activePage)}`, 'en');
    setLinkTag('alternate', `${origin}${toPath('nl', activePage)}`, 'nl');
    setLinkTag('alternate', `${origin}${toPath('en', activePage)}`, 'x-default');
    setLinkTag('icon', mouseIcon);
    setLinkTag('shortcut icon', mouseIcon);
    setLinkTag('apple-touch-icon', mouseIcon);
  }, [activePage, language, t]);

  return (
    <div className="app-shell">
      <header className="site-header">
        <nav className="top-nav">
          <div className="nav-links">
            <button
              className={`nav-btn${activePage === 'home' ? ' nav-btn-active' : ''}`}
              onClick={() => navigate(language, 'home')}
            >
              {t.nav.home}
            </button>
            <button
              className={`nav-btn${activePage === 'cv' ? ' nav-btn-active' : ''}`}
              onClick={() => navigate(language, 'cv')}
            >
              {t.nav.cv}
            </button>
            <button
              className={`nav-btn${activePage === 'contact' ? ' nav-btn-active' : ''}`}
              onClick={() => navigate(language, 'contact')}
            >
              {t.nav.contact}
            </button>
          </div>
          <div className="nav-logo-badge" aria-label="Mouse-It logo">
            <img src={mouseItLogo} alt="Mouse-It logo" className="nav-logo-image" />
          </div>
          <div className="language-control">
            <button
              className="nav-btn language-toggle"
              onClick={() => navigate(language === 'en' ? 'nl' : 'en', activePage)}
              aria-label={language === 'en' ? 'Switch to Dutch' : 'Switch to English'}
              title={language === 'en' ? 'Switch to Dutch' : 'Switch to English'}
            >
              <span aria-hidden="true">{language === 'en' ? 'EN' : 'NL'}</span>
            </button>
          </div>
        </nav>
      </header>

      <main className="content">
        {activePage === 'home' && (
          <HomePage candidateProfile={candidateProfile} availableFocusAreas={availableFocusAreas} />
        )}
        {activePage === 'cv' && (
          <CvPage candidateProfile={candidateProfile} availableFocusAreas={availableFocusAreas} availableTechnologies={availableTechnologies} dataError={dataError} />
        )}
        {activePage === 'contact' && (
          <ContactPage />
        )}
      </main>

      <footer className="footer">
        <p>{t.footer}</p>
      </footer>
    </div>
  );
};

export default App;
